import React from "react";
import { Github, Twitter, Mail, Heart } from "lucide-react";

export function Footer() {
  const currentYear = new Date().getFullYear();

  return (
    <footer 
      className="border-t relative z-20 mt-auto backdrop-blur-xl"
      style={{
        background: `
          linear-gradient(135deg, rgba(203, 108, 230, 0.15) 0%, rgba(168, 85, 247, 0.1) 100%),
          radial-gradient(circle at 50% 100%, rgba(203, 108, 230, 0.12) 0%, transparent 50%),
          rgba(15, 10, 21, 0.9)
        `,
        borderTopColor: 'rgba(203, 108, 230, 0.25)',
        boxShadow: `
          0 -8px 32px rgba(203, 108, 230, 0.15),
          inset 0 1px 0 rgba(203, 108, 230, 0.1)
        `
      }}
    >
      <div className="container mx-auto px-4 py-10 max-w-7xl">
        <div className="flex flex-col md:flex-row justify-between items-center gap-8">
          <div className="flex items-center gap-6">
            <div 
              className="p-3 rounded-xl relative overflow-hidden flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #cb6ce6 0%, #a855f7 50%, #9333ea 100%)',
                boxShadow: `
                  0 8px 25px rgba(203, 108, 230, 0.4),
                  inset 0 1px 0 rgba(255, 255, 255, 0.3),
                  0 0 0 1px rgba(203, 108, 230, 0.3)
                `,
                width: '48px',
                height: '48px',
              }}
            >
              <span
                className="relative z-10"
                style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#ffffff',
                  fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                }}
              >
                Ø
              </span>
              <div 
                className="absolute inset-0 opacity-40"
                style={{
                  background: 'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.3) 0%, transparent 70%)',
                  filter: 'blur(10px)'
                }}
              />
            </div>
            <div>
              <h3 
                className="text-xl"
                style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  fontWeight: '500',
                  letterSpacing: '-0.01em'
                }}
              >
                Øfun
              </h3>
              <div className="flex items-center gap-2 text-sm">
                <span style={{ color: '#94a3b8' }}>Made with</span>
                <Heart className="w-4 h-4" style={{ color: '#ef4444' }} />
                <span style={{ color: '#94a3b8' }}>© {currentYear}</span>
              </div>
            </div>
          </div>
          
          <div className="flex gap-4">
            {[
              { icon: Github, label: "GitHub", color: "#cb6ce6", href: "https://github.com/zerofunxyz/Ofun" },
              { icon: Twitter, label: "Twitter", color: "#cb6ce6", href: "https://x.com/zerofunxyz" },
              { icon: Mail, label: "Email", color: "#cb6ce6", href: "mailto:contact@zerofun.xyz" }
            ].map(({ icon: Icon, label, color, href }) => (
              <a 
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer" 
                className="p-4 rounded-xl transition-all duration-300 relative overflow-hidden group"
                style={{
                  background: 'linear-gradient(135deg, rgba(203, 108, 230, 0.15) 0%, rgba(168, 85, 247, 0.1) 100%)',
                  boxShadow: `
                    0 4px 15px rgba(203, 108, 230, 0.2),
                    inset 0 1px 0 rgba(255, 255, 255, 0.08),
                    0 0 0 1px rgba(203, 108, 230, 0.25)
                  `
                }}
                aria-label={label}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-3px) scale(1.05)';
                  e.currentTarget.style.boxShadow = `
                    0 12px 30px rgba(203, 108, 230, 0.4),
                    inset 0 1px 0 rgba(255, 255, 255, 0.15),
                    0 0 0 1px ${color}60,
                    0 0 30px ${color}40
                  `;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0px) scale(1)';
                  e.currentTarget.style.boxShadow = `
                    0 4px 15px rgba(203, 108, 230, 0.2),
                    inset 0 1px 0 rgba(255, 255, 255, 0.08),
                    0 0 0 1px rgba(203, 108, 230, 0.25)
                  `;
                }}
              >
                <Icon 
                  className="w-5 h-5 transition-all duration-300 relative z-10" 
                  style={{ color: '#cb6ce6' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#ffffff';
                    e.currentTarget.style.filter = `drop-shadow(0 0 8px ${color}80)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#cb6ce6';
                    e.currentTarget.style.filter = 'none';
                  }}
                />
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                  style={{
                    background: `linear-gradient(135deg, ${color}50 0%, transparent 100%)`
                  }}
                />
              </a>
            ))}
          </div>
        </div>
        
        <div 
          className="mt-8 pt-8 text-center border-t"
          style={{
            borderTopColor: 'rgba(203, 108, 230, 0.2)'
          }}
        >
          <p 
            className="text-sm"
            style={{
              color: '#64748b',
              fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
              fontWeight: '400',
              letterSpacing: '0.01em'
            }}
          >
            A modular blockchain built with SignalChain design for scalability, security, and decentralization
          </p>
        </div>
      </div>
    </footer>
  );
}
